import React, { useState } from 'react';
import Breadcrumbs from '../../components/Breadcrumbs';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  Alert,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Divider,
  Paper,
  Stack,
  Tabs,
  Tab,
  useTheme,
  useMediaQuery
} from '@mui/material';
import {
  Add as AddIcon,
  Download as DownloadIcon,
  Visibility as ViewIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Inventory as ProductIcon,
  ShoppingCart as POIcon,
  Remove as RemoveIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
  LocalShipping as ShipIcon
} from '@mui/icons-material';

// Mock shipping addresses for Bare Biologics
const BARE_SHIPPING_ADDRESSES = [
  {
    id: 'SHIP-001',
    name: 'Bare Biologics HQ',
    address: '123 Science Drive',
    city: 'Austin',
    state: 'TX',
    zipCode: '78701',
    contactName: 'Operations Manager',
    contactPhone: '(512) 555-0123',
    isDefault: true,
    notes: 'Main receiving dock - Business hours only'
  },
  {
    id: 'SHIP-002',
    name: 'Bare Biologics Lab',
    address: '456 Research Blvd, Suite 200',
    city: 'Austin',
    state: 'TX',
    zipCode: '78705',
    contactName: 'Lab Supervisor',
    contactPhone: '(512) 555-0124',
    isDefault: false,
    notes: 'Temperature-controlled receiving required'
  },
  {
    id: 'SHIP-003',
    name: 'Bare Biologics Warehouse',
    address: '789 Industrial Way',
    city: 'Round Rock',
    state: 'TX',
    zipCode: '78664',
    contactName: 'Warehouse Manager',
    contactPhone: '(512) 555-0125',
    isDefault: false,
    notes: 'Large shipments and bulk storage'
  }
];

// Mock product catalog for Bare Biologics - Empty by default
const BARE_PRODUCT_CATALOG = [];

const PurchaseOrders = ({ currentCompany }) => {
  const theme = useTheme();
  const isSm = useMediaQuery(theme.breakpoints.down('sm'));

  const [activeTab, setActiveTab] = useState(0);
  const [openPODialog, setOpenPODialog] = useState(false);
  const [openProductDialog, setOpenProductDialog] = useState(false);
  const [activeStep, setActiveStep] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');

  // Product Management State
  const [products, setProducts] = useState(BARE_PRODUCT_CATALOG);
  const [newProduct, setNewProduct] = useState({
    id: '', // Product Code
    name: '', // Product
    location: '', // Location
    supplier: '', // Vendor
    unitPrice: '', // Unit Cost
    unit: '', // Unit
    status: 'active' // Status: active or inactive
  });
  const [isEditingProduct, setIsEditingProduct] = useState(false);
  const [editingProductId, setEditingProductId] = useState(null);

  // Shipping Address Management State
  const [shippingAddresses, setShippingAddresses] = useState(BARE_SHIPPING_ADDRESSES);
  const [openShippingDialog, setOpenShippingDialog] = useState(false);
  const [newShippingAddress, setNewShippingAddress] = useState({
    name: '',
    address: '',
    city: '',
    state: '',
    zipCode: '',
    contactName: '',
    contactPhone: '',
    notes: '',
    isDefault: false
  });

  // PO Creation State
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [poFormData, setPOFormData] = useState({
    vendor: '',
    shipToAddress: '',
    poNumber: '',
    notes: '',
    expectedDeliveryDate: ''
  });

  const [purchaseOrders, setPurchaseOrders] = useState([
    {
      id: 'PO-001',
      company: 'bare',
      vendor: 'Britecyte',
      products: [
        {
          id: 'LIP-001',
          name: 'Lipoderma Base Material',
          quantity: 100,
          unitPrice: 45.00,
          total: 4500.00
        }
      ],
      totalAmount: 4500.00,
      shipToAddress: 'Bare Biologics HQ, 123 Science Dr',
      poNumber: 'BB-2024-001',
      status: 'Pending',
      date: '2024-01-15',
      notes: 'Standard monthly order'
    }
  ]);

  const categories = ['Raw Materials', 'Processing Supplies', 'Quality Assurance', 'Medical Supplies', 'Storage', 'Packaging'];
  const suppliers = ['Britecyte', 'Lab Services Inc', 'Medical Supply Co', 'Lab Equipment Plus', 'Cold Chain Solutions', 'Other'];

  // Product Management Functions
  const handleSaveProduct = () => {
    const productToSave = {
      ...newProduct,
      unitPrice: parseFloat(newProduct.unitPrice),
      status: newProduct.status || 'active'
    };
    if (isEditingProduct && editingProductId) {
      setProducts(products.map(p => (p.id === editingProductId ? productToSave : p)));
    } else {
      const id = newProduct.id && newProduct.id.trim() !== '' ? newProduct.id : `PROD-${Date.now()}`;
      setProducts([...products, { ...productToSave, id }]);
    }
    setNewProduct({ id: '', name: '', location: '', supplier: '', unitPrice: '', unit: '', status: 'active' });
    setIsEditingProduct(false);
    setEditingProductId(null);
    setOpenProductDialog(false);
  };

  const handleDeleteProduct = (productId) => {
    if (window.confirm('Are you sure you want to delete this product?')) {
      setProducts(products.filter(p => p.id !== productId));
    }
  };

  const handleOpenAddProduct = () => {
    setIsEditingProduct(false);
    setEditingProductId(null);
    setNewProduct({ id: '', name: '', location: '', supplier: '', unitPrice: '', unit: '', status: 'active' });
    setOpenProductDialog(true);
  };

  const handleOpenEditProduct = (product) => {
    setIsEditingProduct(true);
    setEditingProductId(product.id);
    setNewProduct({
      id: product.id,
      name: product.name,
      location: product.location || '',
      supplier: product.supplier,
      unitPrice: product.unitPrice,
      unit: product.unit,
      status: product.status || 'active'
    });
    setOpenProductDialog(true);
  };

  // Shipping Address Management Functions
  const handleAddShippingAddress = () => {
    const address = {
      id: `SHIP-${Date.now()}`,
      ...newShippingAddress
    };

    // If this is set as default, remove default from others
    let updatedAddresses = shippingAddresses;
    if (newShippingAddress.isDefault) {
      updatedAddresses = shippingAddresses.map(addr => ({ ...addr, isDefault: false }));
    }

    setShippingAddresses([...updatedAddresses, address]);
    setNewShippingAddress({
      name: '',
      address: '',
      city: '',
      state: '',
      zipCode: '',
      contactName: '',
      contactPhone: '',
      notes: '',
      isDefault: false
    });
    setOpenShippingDialog(false);
  };

  const handleSetDefaultAddress = (addressId) => {
    setShippingAddresses(shippingAddresses.map(addr => ({
      ...addr,
      isDefault: addr.id === addressId
    })));
  };

  // PO Creation Functions
  const handleAddProductToPO = (product) => {
    const existingIndex = selectedProducts.findIndex(p => p.id === product.id);
    if (existingIndex === -1) {
      setSelectedProducts([...selectedProducts, {
        ...product,
        quantity: 1,
        unitPrice: product.unitPrice,
        total: product.unitPrice
      }]);
    }
  };

  const handleRemoveProductFromPO = (productId) => {
    setSelectedProducts(selectedProducts.filter(p => p.id !== productId));
  };

  const handleProductUpdate = (productId, field, value) => {
    setSelectedProducts(selectedProducts.map(product => {
      if (product.id === productId) {
        const updatedProduct = { ...product, [field]: value };
        if (field === 'quantity' || field === 'unitPrice') {
          updatedProduct.total = updatedProduct.quantity * updatedProduct.unitPrice;
        }
        return updatedProduct;
      }
      return product;
    }));
  };

  const getTotalPOAmount = () => {
    return selectedProducts.reduce((total, product) => total + product.total, 0);
  };

  // Filter products based on search term
  const filteredProducts = products.filter(product =>
    product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    product.category.toLowerCase().includes(searchTerm.toLowerCase()) ||
    product.supplier.toLowerCase().includes(searchTerm.toLowerCase()) ||
    product.id.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleClearSearch = () => {
    setSearchTerm('');
  };

  const handleSubmitPO = () => {
    const newPO = {
      id: `PO-${Date.now()}`,
      company: currentCompany,
      vendor: poFormData.vendor,
      products: selectedProducts,
      totalAmount: getTotalPOAmount(),
      shipToAddress: poFormData.shipToAddress,
      poNumber: poFormData.poNumber || `BB-${Date.now()}`,
      status: 'Draft',
      date: new Date().toISOString().split('T')[0],
      notes: poFormData.notes,
      expectedDeliveryDate: poFormData.expectedDeliveryDate
    };

    setPurchaseOrders([...purchaseOrders, newPO]);
    setOpenPODialog(false);
    setSelectedProducts([]);
    setPOFormData({
      vendor: '',
      shipToAddress: '',
      poNumber: '',
      notes: '',
      expectedDeliveryDate: ''
    });
    setActiveStep(0);
  };

  const handleNext = () => {
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const steps = [
    'Select Products',
    'Configure Quantities & Pricing',
    'PO Details & Review'
  ];

  return (
    <Box>
      <Breadcrumbs />
      {/* Header */}
      <Box sx={{
        mb: 3,
        mt: { xs: '50px', md: 0 }, // push down on mobile to sit below hamburger
        background: 'linear-gradient(135deg, #039582 0%, #027A6B 100%)',
        p: 3,
        borderRadius: 2,
        color: 'white',
        pl: { xs: '16px', md: 3 },
        transition: 'margin 0.2s ease',
      }}>
        <Typography variant="h4" sx={{ fontWeight: 700, color: 'white', mb: 1 }}>
          Purchase Orders
        </Typography>
        <Typography variant="body1" sx={{ color: 'rgba(255,255,255,0.9)' }}>
        Enterprise-grade purchase order control system
        </Typography>
      </Box>
      <Box sx={{
      }}>
        
        {/* Tabs */}
        <Card sx={{ mb: 3, overflow: 'hidden' }}>
          <Tabs
            value={activeTab}
            onChange={(e, v) => setActiveTab(v)}
            variant="scrollable"
            scrollButtons="auto"
            allowScrollButtonsMobile
            textColor="primary"
            indicatorColor="primary"
            sx={{
              '& .MuiTab-root': {
                minWidth: 'auto',
                px: { xs: 1.5, md: 3 },
                fontSize: { xs: '0.92rem', md: '1rem' },
                textTransform: 'none',
                whiteSpace: 'nowrap',
                gap: 1,
              },
              '& .MuiTabs-scrollButtons.Mui-disabled': { opacity: 0.35 },
            }}
          >
            <Tab icon={<POIcon />} iconPosition="start" label="Active Purchase Orders" />
            <Tab icon={<ProductIcon />} iconPosition="start" label="Product Catalog" />
            <Tab icon={<ShipIcon />} iconPosition="start" label="Ship To Addresses" />
          </Tabs>
        </Card>

        {/* Active Purchase Orders Tab */}
        {activeTab === 0 && (
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Active Purchase Orders
                </Typography>
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={() => setOpenPODialog(true)}
                  sx={{
                    bgcolor: '#039582',
                    '&:hover': { bgcolor: '#027A6B' },
                    fontWeight: 600,
                    whiteSpace: 'nowrap',
                  }}
                >
                  Create New PO
                </Button>
              </Box>

              {isSm ? (
                <Stack spacing={1.5} sx={{ p: 1 }}>
                  {purchaseOrders.map((po) => (
                    <Paper key={po.id} variant="outlined" sx={{ p: 2 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 700, fontFamily: 'monospace' }}>{po.poNumber}</Typography>
                        <Chip 
                          label={po.status} 
                          size="small" 
                          color={po.status === 'In Play' || po.status === 'Pending' ? 'warning' : po.status === 'Received' ? 'info' : po.status === 'Approved' ? 'success' : 'default'} 
                        />
                      </Box>
                      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 1 }}>
                        <Typography variant="body2" color="text.secondary">{po.vendor}</Typography>
                        <Typography variant="body2" color="text.secondary">{po.date}</Typography>
                        <Typography variant="body2" color="text.secondary">Total: ${po.totalAmount ? po.totalAmount.toFixed(2) : (po.quantity * po.purchasePrice).toFixed(2)}</Typography>
                      </Box>
                      <Box sx={{ mb: 1 }}>
                        {po.products && po.products.length > 0 ? (
                          <Typography variant="body2" color="text.secondary">
                            {po.products[0].name} (x{po.products[0].quantity}){po.products.length > 1 ? ` +${po.products.length - 1} more` : ''}
                          </Typography>
                        ) : (
                          <Typography variant="body2" color="text.secondary">-</Typography>
                        )}
                      </Box>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <IconButton size="small" sx={{ color: '#64748b' }}>
                          <ViewIcon />
                        </IconButton>
                        <IconButton size="small" sx={{ color: '#64748b' }}>
                          <DownloadIcon />
                        </IconButton>
                      </Box>
                    </Paper>
                  ))}
                </Stack>
              ) : (
                <TableContainer sx={{ width: '100%', overflowX: 'auto' }}>
                  <Table sx={{ minWidth: 900 }}>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 600 }}>PO Number</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Vendor</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Products</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Total Amount</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Date</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {purchaseOrders.map((po) => (
                        <TableRow key={po.id}>
                          <TableCell>{po.poNumber}</TableCell>
                          <TableCell>{po.vendor}</TableCell>
                          <TableCell>
                            <Box>
                              {po.products ? (
                                po.products.map((product, index) => (
                                  <Typography key={index} variant="body2" sx={{ fontSize: '0.875rem' }}>
                                    {product.name} (x{product.quantity})
                                  </Typography>
                                ))
                              ) : (
                                <Typography variant="body2">{po.product} (x{po.quantity})</Typography>
                              )}
                            </Box>
                          </TableCell>
                          <TableCell>
                            ${po.totalAmount ? po.totalAmount.toFixed(2) : (po.quantity * po.purchasePrice).toFixed(2)}
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={po.status}
                              color={po.status === 'In Play' || po.status === 'Pending' ? 'warning' : po.status === 'Received' ? 'info' : po.status === 'Approved' ? 'success' : 'default'}
                              size="small"
                            />
                          </TableCell>
                          <TableCell>{po.date}</TableCell>
                          <TableCell>
                            <IconButton size="small" sx={{ color: '#64748b' }}>
                              <ViewIcon />
                            </IconButton>
                            <IconButton size="small" sx={{ color: '#64748b' }}>
                              <DownloadIcon />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </CardContent>
          </Card>
        )}

        {/* Product Catalog Tab */}
        {activeTab === 1 && (
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Product Catalog
                </Typography>
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={handleOpenAddProduct}
                  sx={{
                    bgcolor: '#039582',
                    '&:hover': { bgcolor: '#027A6B' },
                    fontWeight: 600
                  }}
                >
                  Add Product
                </Button>
              </Box>

              {isSm ? (
                <Stack spacing={1.5} sx={{ p: 1 }}>
                  {products.map((product) => (
                    <Paper key={product.id} variant="outlined" sx={{ p: 2 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>{product.name}</Typography>
                        <Chip label={product.id} size="small" variant="outlined" />
                      </Box>
                      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 1 }}>
                        <Typography variant="body2" color="text.secondary">Vendor: {product.supplier}</Typography>
                        <Typography variant="body2" color="text.secondary">Location: {product.location || '-'}</Typography>
                        <Typography variant="body2" color="text.secondary">Unit: {product.unit}</Typography>
                        <Typography variant="body2" color="text.secondary">Cost: ${product.unitPrice.toFixed(2)}</Typography>
                        <Chip 
                          label={product.status === 'active' ? 'Active' : 'Inactive'} 
                          color={product.status === 'active' ? 'success' : 'default'}
                          size="small"
                        />
                      </Box>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <IconButton size="small" sx={{ color: '#5c725c' }} onClick={() => handleOpenEditProduct(product)}>
                          <EditIcon />
                        </IconButton>
                        <IconButton size="small" sx={{ color: '#d32f2f' }} onClick={() => handleDeleteProduct(product.id)}>
                          <DeleteIcon />
                        </IconButton>
                      </Box>
                    </Paper>
                  ))}
                </Stack>
              ) : (
                <TableContainer sx={{ width: '100%', overflowX: 'auto' }}>
                  <Table sx={{ minWidth: 900 }}>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 600 }}>Product Code</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Product</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Location</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Vendor</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Unit Cost</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Unit</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {products.map((product) => (
                        <TableRow key={product.id}>
                          <TableCell>{product.id}</TableCell>
                          <TableCell>{product.name}</TableCell>
                          <TableCell>{product.location || '-'}</TableCell>
                          <TableCell>{product.supplier}</TableCell>
                          <TableCell>${product.unitPrice.toFixed(2)}</TableCell>
                          <TableCell>{product.unit}</TableCell>
                          <TableCell>
                            <Chip 
                              label={product.status === 'active' ? 'Active' : 'Inactive'} 
                              color={product.status === 'active' ? 'success' : 'default'}
                              size="small"
                            />
                          </TableCell>
                          <TableCell>
                            <IconButton size="small" sx={{ color: '#5c725c' }} onClick={() => handleOpenEditProduct(product)}>
                              <EditIcon />
                            </IconButton>
                            <IconButton size="small" sx={{ color: '#d32f2f' }} onClick={() => handleDeleteProduct(product.id)}>
                              <DeleteIcon />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </CardContent>
          </Card>
        )}

        {/* Ship To Addresses Tab */}
        {activeTab === 2 && (
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Ship To Addresses
                </Typography>
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={() => setOpenShippingDialog(true)}
                  sx={{
                    bgcolor: '#5c725c',
                    '&:hover': { bgcolor: '#3c4a3c' }
                  }}
                >
                  Add Address
                </Button>
              </Box>

              <Grid container spacing={3}>
                {shippingAddresses.map((address) => (
                  <Grid item xs={12} md={6} key={address.id}>
                    <Card sx={{
                      border: address.isDefault ? 2 : 1,
                      borderColor: address.isDefault ? '#5c725c' : '#e2e8f0',
                      bgcolor: address.isDefault ? '#f0f4f0' : 'white',
                      position: 'relative'
                    }}>
                      <CardContent>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                          <Typography variant="h6" sx={{ fontWeight: 600, color: '#1e293b' }}>
                            {address.name}
                          </Typography>
                          {address.isDefault && (
                            <Chip
                              label="Default"
                              size="small"
                              sx={{
                                bgcolor: '#5c725c',
                                color: 'white',
                                fontWeight: 600
                              }}
                            />
                          )}
                        </Box>

                        <Typography variant="body2" sx={{ color: '#64748b', mb: 1 }}>
                          <strong>Address:</strong> {address.address}
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#64748b', mb: 1 }}>
                          <strong>City/State/ZIP:</strong> {address.city}, {address.state} {address.zipCode}
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#64748b', mb: 1 }}>
                          <strong>Contact:</strong> {address.contactName}
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#64748b', mb: 2 }}>
                          <strong>Phone:</strong> {address.contactPhone}
                        </Typography>

                        {address.notes && (
                          <Typography variant="body2" sx={{
                            color: '#64748b',
                            fontStyle: 'italic',
                            bgcolor: '#f8fafc',
                            p: 1,
                            borderRadius: 1,
                            mb: 2
                          }}>
                            <strong>Notes:</strong> {address.notes}
                          </Typography>
                        )}

                        <Box sx={{ display: 'flex', gap: 1 }}>
                          {!address.isDefault && (
                            <Button
                              size="small"
                              variant="outlined"
                              onClick={() => handleSetDefaultAddress(address.id)}
                              sx={{
                                borderColor: '#5c725c',
                                color: '#5c725c',
                                '&:hover': { borderColor: '#3c4a3c', color: '#3c4a3c' }
                              }}
                            >
                              Set Default
                            </Button>
                          )}
                          <IconButton size="small" sx={{ color: '#5c725c' }}>
                            <EditIcon />
                          </IconButton>
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </CardContent>
          </Card>
        )}

        {/* Product Management Dialog */}
        <Dialog
          open={openProductDialog}
          onClose={() => setOpenProductDialog(false)}
          maxWidth="md"
          fullWidth
          fullScreen={isSm}
        >
          <DialogTitle sx={{ bgcolor: '#5c725c', color: 'white' }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <ProductIcon sx={{ mr: 2 }} />
              {isEditingProduct ? 'Edit Product' : 'Add New Product'}
            </Box>
          </DialogTitle>
          <DialogContent sx={{ mt: 2 }}>
            <Grid container spacing={3}>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  label="Product Code"
                  value={newProduct.id}
                  onChange={(e) => setNewProduct({ ...newProduct, id: e.target.value })}
                  required={!isEditingProduct}
                  disabled={isEditingProduct}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  label="Product"
                  value={newProduct.name}
                  onChange={(e) => setNewProduct({ ...newProduct, name: e.target.value })}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  label="Location"
                  value={newProduct.location}
                  onChange={(e) => setNewProduct({ ...newProduct, location: e.target.value })}
                  required
                  placeholder="e.g., HQ, Lab, Warehouse"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth required>
                  <InputLabel>Vendor</InputLabel>
                  <Select
                    value={newProduct.supplier}
                    onChange={(e) => setNewProduct({ ...newProduct, supplier: e.target.value })}
                    label="Vendor"
                  >
                    {suppliers.map(supplier => (
                      <MenuItem key={supplier} value={supplier}>{supplier}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={3}>
                <TextField
                  fullWidth
                  label="Unit Cost"
                  type="number"
                  value={newProduct.unitPrice}
                  onChange={(e) => setNewProduct({ ...newProduct, unitPrice: e.target.value })}
                  required
                  InputProps={{ startAdornment: '$' }}
                />
              </Grid>
              <Grid item xs={12} sm={3}>
                <TextField
                  fullWidth
                  label="Unit"
                  value={newProduct.unit}
                  onChange={(e) => setNewProduct({ ...newProduct, unit: e.target.value })}
                  required
                  placeholder="e.g., vial, kit, each"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth required>
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={newProduct.status || 'active'}
                    onChange={(e) => setNewProduct({ ...newProduct, status: e.target.value })}
                    label="Status"
                  >
                    <MenuItem value="active">Active</MenuItem>
                    <MenuItem value="inactive">Inactive</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions sx={{ p: 3 }}>
            <Button onClick={() => setOpenProductDialog(false)}>Cancel</Button>
            <Button
              onClick={handleSaveProduct}
              variant="contained"
              sx={{ bgcolor: '#5c725c', '&:hover': { bgcolor: '#3c4a3c' } }}
            >
              {isEditingProduct ? 'Save Changes' : 'Add Product'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Shipping Address Management Dialog */}
        <Dialog
          open={openShippingDialog}
          onClose={() => setOpenShippingDialog(false)}
          maxWidth="md"
          fullWidth
          fullScreen={isSm}
        >
          <DialogTitle sx={{ bgcolor: '#5c725c', color: 'white' }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <ShipIcon sx={{ mr: 2 }} />
              Add New Shipping Address
            </Box>
          </DialogTitle>
          <DialogContent sx={{ mt: 2 }}>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Location Name"
                  value={newShippingAddress.name}
                  onChange={(e) => setNewShippingAddress({ ...newShippingAddress, name: e.target.value })}
                  required
                  placeholder="e.g., Main Lab, Warehouse, HQ"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Street Address"
                  value={newShippingAddress.address}
                  onChange={(e) => setNewShippingAddress({ ...newShippingAddress, address: e.target.value })}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  label="City"
                  value={newShippingAddress.city}
                  onChange={(e) => setNewShippingAddress({ ...newShippingAddress, city: e.target.value })}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  select
                  label="State"
                  value={newShippingAddress.state}
                  onChange={(e) => setNewShippingAddress({ ...newShippingAddress, state: e.target.value })}
                  required
                >
                  <MenuItem value="TX">Texas</MenuItem>
                  <MenuItem value="CA">California</MenuItem>
                  <MenuItem value="NY">New York</MenuItem>
                  <MenuItem value="FL">Florida</MenuItem>
                  <MenuItem value="IL">Illinois</MenuItem>
                  <MenuItem value="PA">Pennsylvania</MenuItem>
                  <MenuItem value="OH">Ohio</MenuItem>
                  <MenuItem value="GA">Georgia</MenuItem>
                  <MenuItem value="NC">North Carolina</MenuItem>
                  <MenuItem value="MI">Michigan</MenuItem>
                </TextField>
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  label="ZIP Code"
                  value={newShippingAddress.zipCode}
                  onChange={(e) => setNewShippingAddress({ ...newShippingAddress, zipCode: e.target.value })}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Contact Name"
                  value={newShippingAddress.contactName}
                  onChange={(e) => setNewShippingAddress({ ...newShippingAddress, contactName: e.target.value })}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Contact Phone"
                  value={newShippingAddress.contactPhone}
                  onChange={(e) => setNewShippingAddress({ ...newShippingAddress, contactPhone: e.target.value })}
                  required
                  placeholder="(555) 123-4567"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Special Instructions"
                  value={newShippingAddress.notes}
                  onChange={(e) => setNewShippingAddress({ ...newShippingAddress, notes: e.target.value })}
                  multiline
                  rows={2}
                  placeholder="e.g., Business hours only, Temperature-controlled required"
                />
              </Grid>
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <input
                    type="checkbox"
                    id="defaultAddress"
                    checked={newShippingAddress.isDefault}
                    onChange={(e) => setNewShippingAddress({ ...newShippingAddress, isDefault: e.target.checked })}
                    style={{ marginRight: 8 }}
                  />
                  <Typography variant="body2" sx={{ color: '#64748b' }}>
                    Set as default shipping address
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions sx={{ p: 3 }}>
            <Button onClick={() => setOpenShippingDialog(false)}>Cancel</Button>
            <Button
              onClick={handleAddShippingAddress}
              variant="contained"
              sx={{ bgcolor: '#5c725c', '&:hover': { bgcolor: '#3c4a3c' } }}
            >
              Add Address
            </Button>
          </DialogActions>
        </Dialog>

        {/* Multi-Product PO Creation Dialog */}
        <Dialog
          open={openPODialog}
          onClose={() => setOpenPODialog(false)}
          maxWidth="lg"
          fullWidth
          fullScreen={isSm}
        >
          <DialogTitle sx={{ bgcolor: '#5c725c', color: 'white' }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <POIcon sx={{ mr: 2 }} />
              Create New Purchase Order
            </Box>
          </DialogTitle>
          <DialogContent sx={{ mt: 2 }}>
            <Stepper activeStep={activeStep} orientation="vertical">
              {/* Step 1: Select Products */}
              <Step>
                <StepLabel>Select Products</StepLabel>
                <StepContent>
                  <Typography variant="body2" sx={{ mb: 2, color: '#64748b' }}>
                    Choose products to include in this purchase order
                  </Typography>

                  {/* Selected Products */}
                  {selectedProducts.length > 0 && (
                    <Card sx={{ mb: 3, bgcolor: '#f0fdfa' }}>
                      <CardContent>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2 }}>
                          Selected Products ({selectedProducts.length})
                        </Typography>
                        <List dense>
                          {selectedProducts.map((product) => (
                            <ListItem key={product.id}>
                              <ListItemText
                                primary={product.name}
                                secondary={`${product.supplier} - $${product.unitPrice.toFixed(2)}/${product.unit}`}
                              />
                              <ListItemSecondaryAction>
                                <IconButton
                                  size="small"
                                  onClick={() => handleRemoveProductFromPO(product.id)}
                                  sx={{ color: '#ef4444' }}
                                >
                                  <RemoveIcon />
                                </IconButton>
                              </ListItemSecondaryAction>
                            </ListItem>
                          ))}
                        </List>
                      </CardContent>
                    </Card>
                  )}

                  {/* Search Bar */}
                  <Card sx={{ mb: 3, bgcolor: '#f8fafc', border: '1px solid #e2e8f0' }}>
                    <CardContent sx={{ p: 3 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <SearchIcon sx={{ color: '#64748b' }} />
                        <TextField
                          fullWidth
                          placeholder="Search products by name, category, supplier, or ID..."
                          value={searchTerm}
                          onChange={(e) => setSearchTerm(e.target.value)}
                          variant="outlined"
                          size="small"
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              bgcolor: 'white',
                              '&:hover': {
                                '& .MuiOutlinedInput-notchedOutline': {
                                  borderColor: '#5c725c'
                                }
                              },
                              '&.Mui-focused': {
                                '& .MuiOutlinedInput-notchedOutline': {
                                  borderColor: '#5c725c'
                                }
                              }
                            }
                          }}
                        />
                        {searchTerm && (
                          <IconButton
                            onClick={handleClearSearch}
                            sx={{
                              color: '#64748b',
                              '&:hover': { color: '#5c725c', bgcolor: '#f0f4f0' }
                            }}
                          >
                            <ClearIcon />
                          </IconButton>
                        )}
                      </Box>
                      {searchTerm && (
                        <Typography variant="body2" sx={{ mt: 2, color: '#64748b' }}>
                          Showing {filteredProducts.length} of {products.length} products
                          {filteredProducts.length === 0 && (
                            <span style={{ color: '#ef4444' }}> - No products found</span>
                          )}
                        </Typography>
                      )}
                    </CardContent>
                  </Card>

                  {/* Available Products */}
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2 }}>
                    Available Products {searchTerm && `(${filteredProducts.length} results)`}
                  </Typography>
                  {filteredProducts.length > 0 ? (
                    <Card>
                      <TableContainer sx={{ maxHeight: 400 }}>
                        <Table stickyHeader>
                          <TableHead>
                            <TableRow>
                              <TableCell sx={{ fontWeight: 600, bgcolor: '#f8fafc' }}>Product</TableCell>
                              <TableCell sx={{ fontWeight: 600, bgcolor: '#f8fafc' }}>Category</TableCell>
                              <TableCell sx={{ fontWeight: 600, bgcolor: '#f8fafc' }}>Supplier</TableCell>
                              <TableCell sx={{ fontWeight: 600, bgcolor: '#f8fafc' }}>Price</TableCell>
                              <TableCell sx={{ fontWeight: 600, bgcolor: '#f8fafc', textAlign: 'center' }}>Action</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {filteredProducts.map((product) => {
                              const isSelected = selectedProducts.some(p => p.id === product.id);
                              return (
                                <TableRow
                                  key={product.id}
                                  sx={{
                                    bgcolor: isSelected ? '#f0fdfa' : 'white',
                                    '&:hover': { bgcolor: isSelected ? '#e6fffa' : '#f8fafc' },
                                    transition: 'background-color 0.2s ease'
                                  }}
                                >
                                  <TableCell>
                                    <Box>
                                      <Typography variant="body2" sx={{ fontWeight: 600, color: '#1e293b' }}>
                                        {product.name}
                                      </Typography>
                                      <Typography variant="caption" sx={{ color: '#64748b' }}>
                                        ID: {product.id}
                                      </Typography>
                                    </Box>
                                  </TableCell>
                                  <TableCell>
                                    <Chip
                                      label={product.category}
                                      size="small"
                                      variant="outlined"
                                      sx={{
                                        fontSize: '0.75rem',
                                        bgcolor: isSelected ? '#0d9488' : 'transparent',
                                        color: isSelected ? 'white' : '#64748b',
                                        borderColor: isSelected ? '#0d9488' : '#cbd5e1'
                                      }}
                                    />
                                  </TableCell>
                                  <TableCell>
                                    <Typography variant="body2" sx={{ color: '#64748b' }}>
                                      {product.supplier}
                                    </Typography>
                                  </TableCell>
                                  <TableCell>
                                    <Typography variant="body2" sx={{ fontWeight: 600, color: '#1e293b' }}>
                                      ${product.unitPrice.toFixed(2)}/{product.unit}
                                    </Typography>
                                  </TableCell>
                                  <TableCell sx={{ textAlign: 'center' }}>
                                    <Button
                                      variant={isSelected ? "outlined" : "contained"}
                                      size="small"
                                      onClick={() => isSelected ? handleRemoveProductFromPO(product.id) : handleAddProductToPO(product)}
                                      sx={isSelected ? {
                                        color: '#ef4444',
                                        borderColor: '#ef4444',
                                        '&:hover': { bgcolor: '#fef2f2' },
                                        minWidth: 80
                                      } : {
                                        bgcolor: '#5c725c',
                                        '&:hover': { bgcolor: '#3c4a3c' },
                                        minWidth: 80
                                      }}
                                    >
                                      {isSelected ? 'Remove' : 'Add'}
                                    </Button>
                                  </TableCell>
                                </TableRow>
                              );
                            })}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </Card>
                  ) : (
                    <Card sx={{ p: 6, textAlign: 'center', bgcolor: '#f8fafc' }}>
                      <SearchIcon sx={{ fontSize: 48, color: '#cbd5e1', mb: 2 }} />
                      <Typography variant="h6" sx={{ color: '#64748b', mb: 1 }}>
                        No products found
                      </Typography>
                      <Typography variant="body2" sx={{ color: '#94a3b8' }}>
                        Try adjusting your search terms or{' '}
                        <Button
                          variant="text"
                          size="small"
                          onClick={handleClearSearch}
                          sx={{ color: '#5c725c', p: 0, minWidth: 'auto' }}
                        >
                          clear the search
                        </Button>
                      </Typography>
                    </Card>
                  )}

                  <Box sx={{ mt: 3 }}>
                    <Button
                      variant="contained"
                      onClick={handleNext}
                      disabled={selectedProducts.length === 0}
                      sx={{
                        bgcolor: '#5c725c',
                        '&:hover': { bgcolor: '#3c4a3c' }
                      }}
                    >
                      Next: Configure Quantities
                    </Button>
                  </Box>
                </StepContent>
              </Step>

              {/* Step 2: Configure Quantities & Pricing */}
              <Step>
                <StepLabel>Configure Quantities & Pricing</StepLabel>
                <StepContent>
                  <Typography variant="body2" sx={{ mb: 3, color: '#64748b' }}>
                    Set quantities and adjust pricing for each product
                  </Typography>

                  <TableContainer component={Paper}>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 600 }}>Product</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>Quantity</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>Unit Price</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>Total</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {selectedProducts.map((product) => (
                          <TableRow key={product.id}>
                            <TableCell>
                              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                {product.name}
                              </Typography>
                              <Typography variant="caption" sx={{ color: '#64748b' }}>
                                {product.supplier}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <TextField
                                type="number"
                                value={product.quantity}
                                onChange={(e) => handleProductUpdate(product.id, 'quantity', parseInt(e.target.value) || 0)}
                                size="small"
                                sx={{ width: 80 }}
                                inputProps={{ min: 1 }}
                              />
                            </TableCell>
                            <TableCell>
                              <TextField
                                type="number"
                                value={product.unitPrice}
                                onChange={(e) => handleProductUpdate(product.id, 'unitPrice', parseFloat(e.target.value) || 0)}
                                size="small"
                                sx={{ width: 100 }}
                                InputProps={{ startAdornment: '$' }}
                                inputProps={{ min: 0, step: 0.01 }}
                              />
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                ${product.total.toFixed(2)}
                              </Typography>
                            </TableCell>
                          </TableRow>
                        ))}
                        <TableRow>
                          <TableCell colSpan={3} sx={{ fontWeight: 600, bgcolor: '#f8fafc' }}>
                            Total PO Amount:
                          </TableCell>
                          <TableCell sx={{ fontWeight: 700, bgcolor: '#f8fafc', fontSize: '1.1rem' }}>
                            ${getTotalPOAmount().toFixed(2)}
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>

                  <Box sx={{ mt: 3, display: 'flex', gap: 2 }}>
                    <Button onClick={handleBack}>
                      Back
                    </Button>
                    <Button
                      variant="contained"
                      onClick={handleNext}
                      sx={{
                        bgcolor: '#5c725c',
                        '&:hover': { bgcolor: '#3c4a3c' }
                      }}
                    >
                      Next: PO Details
                    </Button>
                  </Box>
                </StepContent>
              </Step>

              {/* Step 3: PO Details & Review */}
              <Step>
                <StepLabel>PO Details & Review</StepLabel>
                <StepContent>
                  <Typography variant="body2" sx={{ mb: 3, color: '#64748b' }}>
                    Complete purchase order details and review before submission
                  </Typography>

                  <Grid container spacing={3}>
                    <Grid item xs={12} sm={6}>
                      <FormControl fullWidth required>
                        <InputLabel>Vendor</InputLabel>
                        <Select
                          value={poFormData.vendor}
                          onChange={(e) => setPOFormData({ ...poFormData, vendor: e.target.value })}
                          label="Vendor"
                        >
                          {suppliers.map(vendor => (
                            <MenuItem key={vendor} value={vendor}>{vendor}</MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="PO Number"
                        value={poFormData.poNumber}
                        onChange={(e) => setPOFormData({ ...poFormData, poNumber: e.target.value })}
                        placeholder="Auto-generated if left blank"
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <FormControl fullWidth required>
                        <InputLabel>Ship To Address</InputLabel>
                        <Select
                          value={poFormData.shipToAddress}
                          onChange={(e) => setPOFormData({ ...poFormData, shipToAddress: e.target.value })}
                          label="Ship To Address"
                        >
                          {shippingAddresses.map(address => (
                            <MenuItem key={address.id} value={`${address.name} - ${address.address}, ${address.city}, ${address.state} ${address.zipCode}`}>
                              <Box>
                                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                  {address.name} {address.isDefault && '(Default)'}
                                </Typography>
                                <Typography variant="caption" sx={{ color: '#64748b' }}>
                                  {address.address}, {address.city}, {address.state} {address.zipCode}
                                </Typography>
                              </Box>
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                      <Typography variant="caption" sx={{ color: '#64748b', mt: 1 }}>
                        Manage shipping addresses in the "Ship To Addresses" tab
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Expected Delivery Date"
                        type="date"
                        value={poFormData.expectedDeliveryDate}
                        onChange={(e) => setPOFormData({ ...poFormData, expectedDeliveryDate: e.target.value })}
                        InputLabelProps={{ shrink: true }}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Notes"
                        value={poFormData.notes}
                        onChange={(e) => setPOFormData({ ...poFormData, notes: e.target.value })}
                        multiline
                        rows={3}
                        placeholder="Additional notes or special instructions"
                      />
                    </Grid>
                  </Grid>

                  {/* Review Summary */}
                  <Card sx={{ mt: 3, bgcolor: '#f8fafc' }}>
                    <CardContent>
                      <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                        Order Summary
                      </Typography>
                      <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                          <Typography variant="body2" sx={{ color: '#64748b' }}>
                            <strong>Products:</strong> {selectedProducts.length} items
                          </Typography>
                          <Typography variant="body2" sx={{ color: '#64748b' }}>
                            <strong>Total Amount:</strong> ${getTotalPOAmount().toFixed(2)}
                          </Typography>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <Typography variant="body2" sx={{ color: '#64748b' }}>
                            <strong>Vendor:</strong> {poFormData.vendor || 'Not selected'}
                          </Typography>
                          <Typography variant="body2" sx={{ color: '#64748b' }}>
                            <strong>PO Number:</strong> {poFormData.poNumber || 'Auto-generated'}
                          </Typography>
                        </Grid>
                      </Grid>
                    </CardContent>
                  </Card>

                  <Box sx={{ mt: 3, display: 'flex', gap: 2 }}>
                    <Button onClick={handleBack}>
                      Back
                    </Button>
                    <Button
                      variant="contained"
                      onClick={handleSubmitPO}
                      disabled={!poFormData.vendor || !poFormData.shipToAddress}
                      sx={{
                        bgcolor: '#5c725c',
                        '&:hover': { bgcolor: '#3c4a3c' }
                      }}
                    >
                      Create Purchase Order
                    </Button>
                  </Box>
                </StepContent>
              </Step>
            </Stepper>
          </DialogContent>
          <DialogActions sx={{ p: 2 }}>
            <Button onClick={() => setOpenPODialog(false)}>Cancel</Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Box>
      );
};

      export default PurchaseOrders;
